import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Wraps case-insensitive matches of `term` in the given text with a
 * `<mark class="search-highlight">` so a live search query is visually
 * highlighted in the results (e.g. the Gable catalog search).
 *
 * The input text is HTML-escaped BEFORE the marks are injected, so arbitrary
 * data (column names, descriptions) can never inject markup — the only HTML
 * in the output is the `<mark>` wrappers this pipe controls. The term is
 * regex-escaped too. Pure: Angular memoizes on `(value, term)` identity.
 */
@Pipe({
    name: 'highlight',
    standalone: true,
})
export class HighlightPipe implements PipeTransform {
    constructor(private readonly _sanitizer: DomSanitizer) {}

    transform(value: string | null | undefined, term: string | null | undefined): SafeHtml {
        const text = value ?? '';
        const needle = (term ?? '').trim();

        if (needle === '') {
            return this._sanitizer.bypassSecurityTrustHtml(this._escape(text));
        }

        // Split the RAW text on the needle (capturing group keeps the matched
        // segments), then escape each segment independently and wrap the
        // matches. Matching before escaping avoids highlighting characters
        // *inside* an HTML entity (e.g. searching `&` shouldn't split `&amp;`).
        const pattern = new RegExp(`(${this._escapeRegExp(needle)})`, 'gi');
        const lowerNeedle = needle.toLowerCase();

        const highlighted = text
            .split(pattern)
            .map((segment) => {
                const escaped = this._escape(segment);
                return segment.toLowerCase() === lowerNeedle
                    ? `<mark class="search-highlight">${escaped}</mark>`
                    : escaped;
            })
            .join('');

        return this._sanitizer.bypassSecurityTrustHtml(highlighted);
    }

    private _escape(text: string): string {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    private _escapeRegExp(text: string): string {
        return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
